import { useVenngageE1Gate } from 'hooks/useVenngageE1Gate';
import { PhishingResponseBody } from 'libs/e2api/types';
import { useE2Client } from 'modules/E2ClientProvider';
import { setPhishing } from 'modules/Editor/store/infographSlice';
import { useUser } from 'modules/User';
import { useToast } from 'modules/common/components/Toast';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { isProductionDomain } from 'utils/environment';

import { PhishingShareMethod } from './useDetectPhishing.config';

// Flag to check if we already checked phishing in this session
let checkedPhishingInCurrentSession = false;

// flag to cache the last result from phishing API returning the last object
let checkedPhishingLastResult: PhishingResponseBody = { phishingDetected: false, status: 'ok' };

/**
 * Hook to trigger spoof check of a design when it is shared
 * Returns method to trigger /detectPhishing endpoint with appropriate params
 * If spoof check has already been performed in this session, it will not be performed again
 *
 * @returns detectPhishing: (shareMethod: PhishingShareMethod) => Promise<void>
 */
export const useDetectPhishing = () => {
  const { venngageE2 } = useVenngageE1Gate();
  const { client, infographId: e2DesignId } = useE2Client();
  const { id: e1UUId } = useParams();
  const { email } = useUser();
  const toast = useToast();
  const dispatch = useDispatch();

  const privateLinkId = venngageE2?.privateLinkId;

  const phishingToast = ({ description, title }: { description?: string; title?: string }) => {
    const descriptionObj = description ? { description } : null;
    toast({
      title: title || 'Failed to copy link',
      status: 'error',
      duration: 5000,
      ...descriptionObj,
    });
  };

  const detectPhishing = useCallback(
    async (shareMethod: PhishingShareMethod, createCard = true) => {
      // Already performed spoof check during this session, don't need to do again
      if (checkedPhishingInCurrentSession && createCard) return checkedPhishingLastResult;
      if (!client) return checkedPhishingLastResult;

      try {
        const result = await client.detectPhishing({
          userEmail: email || '',
          designId: e2DesignId || '',
          e1UUId: e1UUId || '',
          designUrl: window.location.href,
          shareMethod,
          shareLink: `${window.location.protocol}//${window.location.host}/pl/${privateLinkId}`,
          createCard: isProductionDomain && createCard,
        });

        if (checkedPhishingLastResult?.phishingDetected !== result?.phishingDetected) {
          // force to check again when the state changes
          checkedPhishingInCurrentSession = false;
          // update infograph to set phishing
          dispatch(setPhishing(result.phishingDetected));
        } else if (createCard) {
          // only cache if the user clicks on sharing
          checkedPhishingInCurrentSession = true;
        }

        checkedPhishingLastResult = result;
        return checkedPhishingLastResult;
      } catch (error) {
        console.error(error);
      }
    },
    [client, email, e2DesignId, e1UUId, privateLinkId, dispatch],
  );

  return { detectPhishing, phishingToast };
};
